import { Constants } from '@babylonjs/core/Engines/constants';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR';
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import { MeshExt } from '@seddi/ui-babylon-core';
import { createEmptyVertexDataDesc } from '@seddi/ui-babylon-smpl';
import { type AvatarTexturesData } from './types/avatarTexturesTypes';

export interface SmplRenderableMaterial {
  material: PBRMaterial;
  textureAlbedo: RawTexture;
  textureBump: RawTexture;
  textureMetallic: RawTexture;
  textureReflectance: RawTexture;
}

export interface SmplRenderable {
  material: SmplRenderableMaterial;
  mesh: Mesh;
}

const TEXTURE_SIZE = 2048;

function createTexture(name: string, scene: Scene): RawTexture {
  const texture = new RawTexture(
    null,
    TEXTURE_SIZE,
    TEXTURE_SIZE,
    Constants.TEXTUREFORMAT_RGBA,
    scene,
    true,
    true,
  );
  texture.name = name;
  return texture;
}

export function createSmplRenderableMaterial(name: string, scene: Scene): SmplRenderableMaterial {
  const textureAlbedo = createTexture(`${name}-albedo`, scene);
  const textureBump = createTexture(`${name}-bump`, scene);
  const textureMetallic = createTexture(`${name}-metallic`, scene);
  const textureReflectance = createTexture(`${name}-reflectance`, scene);
  textureBump.gammaSpace = false;
  textureMetallic.gammaSpace = false;
  textureReflectance.gammaSpace = false;

  const material = new PBRMaterial(name, scene);
  material.albedoTexture = textureAlbedo;
  material.bumpTexture = textureBump;
  material.metallicTexture = textureMetallic;
  material.reflectanceTexture = textureReflectance;
  material.roughness = 1.15;
  material.sideOrientation = 1;
  material.subSurface.isTranslucencyEnabled = true;
  material.subSurface.translucencyIntensity = 0.5;
  material.subSurface.minimumThickness = 0.1;
  material.subSurface.maximumThickness = 0.1;
  material.subSurface.tintColor = new Color3(0.8, 0.4, 0.4);
  material.useRoughnessFromMetallicTextureAlpha = false;
  material.useRoughnessFromMetallicTextureGreen = true;
  // F0 factor incident specular range [0.0, 0.08]
  // material._metallicF0Factor = 2;
  material._metallicF0Factor = 1.5;

  return {
    material,
    textureAlbedo,
    textureBump,
    textureMetallic,
    textureReflectance,
  };
}

/**
 * Update SMPL renderable material.
 * @param material - Avatar renderable materials.
 * @param data - Avatar textures data.
 */
export function updateSmplRenderableMaterial(
  material: SmplRenderableMaterial,
  data: AvatarTexturesData,
): void {
  material.textureAlbedo.update(data.baseColor);
  material.textureBump.update(data.normal);
  material.textureMetallic.update(data.roughness);
  material.textureReflectance.update(data.specular);
}

/**
 * Create SMPL renderable.
 * @param name - Name.
 * @param scene - Engine scene.
 */
export function createSmplRenderable(name: string, scene: Scene): SmplRenderable {
  const mesh = new Mesh(name, scene);
  const material = createSmplRenderableMaterial(name, scene);
  mesh.material = material.material;

  const geometry = createEmptyVertexDataDesc();
  MeshExt.applyGeometry(mesh, geometry, true);
  mesh.setEnabled(false);

  return { material, mesh };
}
